import React, { useState, useEffect } from 'react';
import { EnhancedTimetableParser } from '../Timetable/EnhancedTimetableParser';
import './ParsingAlgorithmSettings.css';

const ParsingAlgorithmSettings = ({ tenantId, schoolName, onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [parsingRules, setParsingRules] = useState({
    // Regex patterns for parsing
    enhancedFormatRegex: '^([^*]+)\\*([^-]+)-<([^>]+)>(?:\\/<([^>]+)>(?:\\/<([^>]+)>(?:\\/<([^>]+)>)?)?)?-(.+)$',
    classRolePatterns: {
      classMaster: ['Class Master', '班主任', 'Class Master:\\s*(.+)', '班主任:\\s*(.+)'],
      viceClassMaster: ['Vice Class Master', '副班主任', 'Vice Class Master:\\s*(.+)', '副班主任:\\s*(.+)']
    },
    subjectTypeRules: {
      admin: ['會議', '同儕'],
      elective: ['X1', 'X2'],
      core: ['S[1-6]', '\\d+[A-Z]']
    },
    // Parsing options
    maxCoTeachers: 4,
    enableClassMasterAssumptions: true,
    enableSubjectTypeDetection: true,
    enableElectiveGroupExtraction: true
  });
  
  const [testContent, setTestContent] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Sample files for testing
  const sampleFiles = {
    teacher: `Based on the timetable image for Teacher 陳嘉俊 (Code: 嘉), here is the extracted text:

## (A) TEACHER TIMETABLE SAMPLES
=======================================================
Teacher: "陳嘉俊" (Code: 嘉)
- **班主任 (Class Master):** 嘉
   **Class Master (班主任):** 6D (from Teacher Assignment)
   **This class Home Room: 208 (from Teacher Assignment)
   
**Basic Information:**
1. **Class Master (班主任):** 6D (Class room: 208)
2. **Teaching Subjects:** 
   - 中國語文 (Chinese Language)
3. **Responsible for Class Lessons:**
   - 星期四 生命及價值教育課 (Life and Values Ed): 6D

**星期一:**
- 第一節 08:10 - 08:45: 6D*中國語文-<嘉>-208
- 第二節 08:45 - 09:20: 6D*中國語文-<嘉>-208
- 第三節 09:20 - 09:55: 5X2*生活技能課(隔周)-<嘉>/<城>-612
- 第四節 10:15 - 10:50: S5*跨班自主學習節-<嘉>-110/Hall
- 第五節 10:50 - 11:25: 4AB3*英國語文-<嘉>/<藍>/<任>-110
- 第六節 11:30 - 12:05: 會議*學生成長會議-<嘉>-TBC
- 第七節 12:05 - 12:40: 同儕*S4 Co-plan-<嘉>-TBC
- 第八節 01:50 - 02:25: 5D*班主任節-<嘉>-612
- 第九節 02:25 - 03:00: ""
- 第十節 03:00 - 03:35: ""`,

    class: `## CLASS TIMETABLE: 6D
**Class Master:** 陳嘉俊 (嘉)
**Home Room:** 208
**Academic Year:** 2024-2025

**Monday:**
- Period 1 (8:10-8:45): 中國語文 - 陳嘉俊 - 208
- Period 2 (8:45-9:20): 數學 - 李老師 - 209
- Period 3 (9:20-9:55): 英文 - 王老師 - 210
- Period 4 (10:15-10:50): 科學 - 張老師 - 211
- Period 5 (10:50-11:25): 歷史 - 劉老師 - 212
- Period 6 (11:30-12:05): 地理 - 陳老師 - 213
- Period 7 (12:05-12:40): 體育 - 體育老師 - 體育館
- Period 8 (1:50-2:25): 音樂 - 音樂老師 - 音樂室
- Period 9 (2:25-3:00): 美術 - 美術老師 - 美術室
- Period 10 (3:00-3:35): 班主任節 - 陳嘉俊 - 208`
  };

  const handleRuleChange = (category, key, value) => {
    setParsingRules(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const handleArrayRuleChange = (category, key, index, value) => {
    setParsingRules(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: prev[category][key].map((item, i) => i === index ? value : item)
      }
    }));
  };

  const addArrayRule = (category, key, value = '') => {
    setParsingRules(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: [...prev[category][key], value]
      }
    }));
  };

  const removeArrayRule = (category, key, index) => {
    setParsingRules(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: prev[category][key].filter((_, i) => i !== index)
      }
    }));
  };

  const testParsing = async () => {
    if (!testContent.trim()) {
      setTestError('Please enter content to test parsing.');
      return;
    }

    setIsTesting(true);
    setTestError('');
    setTestResult(null);

    try {
      // Create setup data with current parsing rules
      const setupData = {
        mode: 'weekday',
        dayPeriods: 15,
        periodPatterns: [
          { period_sequence: 1, time_range: "8:00-8:10", period_node: "班主任節", period_number: 0 },
          { period_sequence: 2, time_range: "8:10-8:45", period_node: "Period 1", period_number: 1 },
          { period_sequence: 3, time_range: "8:45-9:20", period_node: "Period 2", period_number: 2 },
          { period_sequence: 4, time_range: "9:20-9:55", period_node: "Period 3", period_number: 3 },
          { period_sequence: 5, time_range: "9:55-10:15", period_node: "Recess", period_number: 0 },
          { period_sequence: 6, time_range: "10:15-10:50", period_node: "Period 4", period_number: 4 },
          { period_sequence: 7, time_range: "10:50-11:25", period_node: "Period 5", period_number: 5 },
          { period_sequence: 8, time_range: "11:25-11:30", period_node: "Recess", period_number: 0 },
          { period_sequence: 9, time_range: "11:30-12:05", period_node: "Period 6", period_number: 6 },
          { period_sequence: 10, time_range: "12:05-12:40", period_node: "Period 7", period_number: 7 },
          { period_sequence: 11, time_range: "12:40-1:50", period_node: "Lunch", period_number: 0 },
          { period_sequence: 12, time_range: "1:50-2:25", period_node: "Period 8", period_number: 8 },
          { period_sequence: 13, time_range: "2:25-3:00", period_node: "Period 9", period_number: 9 },
          { period_sequence: 14, time_range: "3:00-3:35", period_node: "Period 10", period_number: 10 },
          { period_sequence: 15, time_range: "3:35-3:50", period_node: "班主任節", period_number: 0 }
        ]
      };

      // Initialize parser with current rules
      const parser = new EnhancedTimetableParser(setupData);
      
      // Parse the test content
      const result = parser.parseNewFormatTimetableFile(testContent, 'test');
      
      setTestResult(result);
    } catch (error) {
      console.error('Parsing test error:', error);
      setTestError(error.message);
    } finally {
      setIsTesting(false);
    }
  };

  const loadSampleFile = (type) => {
    setTestContent(sampleFiles[type]);
    setTestResult(null);
    setTestError('');
  };

  const exportConfiguration = () => {
    const config = {
      parsingRules,
      timestamp: new Date().toISOString(),
      version: '1.0.0'
    };
    
    const dataStr = JSON.stringify(config, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `parsing-algorithm-config-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const importConfiguration = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const config = JSON.parse(e.target.result);
          if (config.parsingRules) {
            setParsingRules(config.parsingRules);
          }
        } catch (error) {
          console.error('Error importing configuration:', error);
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="parsing-algorithm-settings">
      <div className="settings-header">
        <div className="header-top">
          {onBack && (
            <button className="back-button" onClick={onBack}>
              ← 返回時間表設定
            </button>
          )}
          <h2>🔧 解析算法設定</h2>
        </div>
        <p>Configure and customize the timetable parsing algorithm for <strong>{schoolName}</strong></p>
        <p className="tenant-info">Tenant ID: {tenantId}</p>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 概覽
        </button>
        <button
          className={`tab ${activeTab === 'rules' ? 'active' : ''}`}
          onClick={() => setActiveTab('rules')}
        >
          ⚙️ 解析規則
        </button>
        <button
          className={`tab ${activeTab === 'testing' ? 'active' : ''}`}
          onClick={() => setActiveTab('testing')}
        >
          🧪 測試工具
        </button>
        <button
          className={`tab ${activeTab === 'examples' ? 'active' : ''}`}
          onClick={() => setActiveTab('examples')}
        >
          📚 格式範例
        </button>
        <button
          className={`tab ${activeTab === 'advanced' ? 'active' : ''}`}
          onClick={() => setActiveTab('advanced')}
        >
          🔬 進階設定
        </button>
      </div>

      <div className="settings-content">
        {activeTab === 'overview' && (
          <div className="overview-section">
            <h3>📊 解析算法概覽</h3>
            <div className="overview-grid">
              <div className="overview-card">
                <h4>🎯 核心功能</h4>
                <ul>
                  <li>✅ 支援最多4位協作教師</li>
                  <li>✅ 自動班主任節期段分配</li>
                  <li>✅ 科目類型智能識別</li>
                  <li>✅ 選修組別自動提取</li>
                  <li>✅ 正則表達式模式匹配</li>
                </ul>
              </div>
              
              <div className="overview-card">
                <h4>📝 支援格式</h4>
                <ul>
                  <li>教師個人時間表腳本</li>
                  <li>班級時間表腳本</li>
                  <li>中文/英文混合格式</li>
                  <li>多種分隔符支援</li>
                  <li>靈活的時間格式</li>
                </ul>
              </div>
              
              <div className="overview-card">
                <h4>🔧 可配置項目</h4>
                <ul>
                  <li>正則表達式模式</li>
                  <li>科目類型規則</li>
                  <li>班級角色識別</li>
                  <li>協作教師數量限制</li>
                  <li>解析選項開關</li>
                </ul>
              </div>
            </div>

            <div className="algorithm-flow">
              <h4>🔄 解析流程</h4>
              <div className="flow-steps">
                <div className="flow-step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h5>文件預處理</h5>
                    <p>讀取腳本文件，識別教師角色和班級信息</p>
                  </div>
                </div>
                <div className="flow-arrow">→</div>
                <div className="flow-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h5>模式匹配</h5>
                    <p>使用正則表達式提取時間表條目</p>
                  </div>
                </div>
                <div className="flow-arrow">→</div>
                <div className="flow-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h5>數據分類</h5>
                    <p>識別科目類型、選修組別、協作教師</p>
                  </div>
                </div>
                <div className="flow-arrow">→</div>
                <div className="flow-step">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <h5>結果輸出</h5>
                    <p>生成結構化時間表數據</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'rules' && (
          <div className="rules-section">
            <h3>⚙️ 解析規則配置</h3>
            
            {/* Enhanced Format Regex */}
            <div className="rule-group">
              <h4>🔍 增強格式正則表達式</h4>
              <p className="rule-description">
                用於解析 "class_id*subject-teacher_name/co-teacher-classroom" 格式
              </p>
              <div className="regex-input-group">
                <input
                  type="text"
                  value={parsingRules.enhancedFormatRegex}
                  onChange={(e) => setParsingRules(prev => ({
                    ...prev,
                    enhancedFormatRegex: e.target.value
                  }))}
                  className="regex-input"
                  placeholder="Enter regex pattern"
                />
                <button
                  onClick={() => {
                    try {
                      new RegExp(parsingRules.enhancedFormatRegex);
                      alert('✅ 正則表達式語法正確');
                    } catch (error) {
                      alert('❌ 正則表達式語法錯誤: ' + error.message);
                    }
                  }}
                  className="validate-btn"
                >
                  ✅ 驗證語法
                </button>
              </div>
            </div>

            {/* Class Role Patterns */}
            <div className="rule-group">
              <h4>👥 班級角色識別模式</h4>
              <div className="role-patterns">
                <div className="pattern-section">
                  <h5>班主任 (Class Master)</h5>
                  {parsingRules.classRolePatterns.classMaster.map((pattern, index) => (
                    <div key={index} className="pattern-input-group">
                      <input
                        type="text"
                        value={pattern}
                        onChange={(e) => handleArrayRuleChange('classRolePatterns', 'classMaster', index, e.target.value)}
                        className="pattern-input"
                        placeholder="Enter pattern"
                      />
                      <button
                        onClick={() => removeArrayRule('classRolePatterns', 'classMaster', index)}
                        className="remove-btn"
                      >
                        ❌
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addArrayRule('classRolePatterns', 'classMaster')}
                    className="add-pattern-btn"
                  >
                    ➕ 添加模式
                  </button>
                </div>

                <div className="pattern-section">
                  <h5>副班主任 (Vice Class Master)</h5>
                  {parsingRules.classRolePatterns.viceClassMaster.map((pattern, index) => (
                    <div key={index} className="pattern-input-group">
                      <input
                        type="text"
                        value={pattern}
                        onChange={(e) => handleArrayRuleChange('classRolePatterns', 'viceClassMaster', index, e.target.value)}
                        className="pattern-input"
                        placeholder="Enter pattern"
                      />
                      <button
                        onClick={() => removeArrayRule('classRolePatterns', 'viceClassMaster', index)}
                        className="remove-btn"
                      >
                        ❌
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addArrayRule('classRolePatterns', 'viceClassMaster')}
                    className="add-pattern-btn"
                  >
                    ➕ 添加模式
                  </button>
                </div>
              </div>
            </div>

            {/* Subject Type Rules */}
            <div className="rule-group">
              <h4>📚 科目類型規則</h4>
              <div className="subject-type-rules">
                <div className="type-section">
                  <h5>管理類型 (Admin)</h5>
                  {parsingRules.subjectTypeRules.admin.map((rule, index) => (
                    <div key={index} className="rule-input-group">
                      <input
                        type="text"
                        value={rule}
                        onChange={(e) => handleArrayRuleChange('subjectTypeRules', 'admin', index, e.target.value)}
                        className="rule-input"
                        placeholder="Enter rule"
                      />
                      <button
                        onClick={() => removeArrayRule('subjectTypeRules', 'admin', index)}
                        className="remove-btn"
                      >
                        ❌
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addArrayRule('subjectTypeRules', 'admin')}
                    className="add-rule-btn"
                  >
                    ➕ 添加規則
                  </button>
                </div>

                <div className="type-section">
                  <h5>選修類型 (Elective)</h5>
                  {parsingRules.subjectTypeRules.elective.map((rule, index) => (
                    <div key={index} className="rule-input-group">
                      <input
                        type="text"
                        value={rule}
                        onChange={(e) => handleArrayRuleChange('subjectTypeRules', 'elective', index, e.target.value)}
                        className="rule-input"
                        placeholder="Enter rule"
                      />
                      <button
                        onClick={() => removeArrayRule('subjectTypeRules', 'elective', index)}
                        className="remove-btn"
                      >
                        ❌
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addArrayRule('subjectTypeRules', 'elective')}
                    className="add-rule-btn"
                  >
                    ➕ 添加規則
                  </button>
                </div>

                <div className="type-section">
                  <h5>核心類型 (Core)</h5>
                  {parsingRules.subjectTypeRules.core.map((rule, index) => (
                    <div key={index} className="rule-input-group">
                      <input
                        type="text"
                        value={rule}
                        onChange={(e) => handleArrayRuleChange('subjectTypeRules', 'core', index, e.target.value)}
                        className="rule-input"
                        placeholder="Enter rule"
                      />
                      <button
                        onClick={() => removeArrayRule('subjectTypeRules', 'core', index)}
                        className="remove-btn"
                      >
                        ❌
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addArrayRule('subjectTypeRules', 'core')}
                    className="add-rule-btn"
                  >
                    ➕ 添加規則
                  </button>
                </div>
              </div>
            </div>

            {/* Parsing Options */}
            <div className="rule-group">
              <h4>🔧 解析選項</h4>
              <div className="parsing-options">
                <div className="option-item">
                  <label>
                    <input
                      type="number"
                      value={parsingRules.maxCoTeachers}
                      onChange={(e) => setParsingRules(prev => ({
                        ...prev,
                        maxCoTeachers: parseInt(e.target.value)
                      }))}
                      min="1"
                      max="10"
                      className="number-input"
                    />
                    最大協作教師數量
                  </label>
                </div>
                
                <div className="option-item">
                  <label>
                    <input
                      type="checkbox"
                      checked={parsingRules.enableClassMasterAssumptions}
                      onChange={(e) => setParsingRules(prev => ({
                        ...prev,
                        enableClassMasterAssumptions: e.target.checked
                      }))}
                    />
                    啟用班主任假設 (自動添加班主任節期段)
                  </label>
                </div>
                
                <div className="option-item">
                  <label>
                    <input
                      type="checkbox"
                      checked={parsingRules.enableSubjectTypeDetection}
                      onChange={(e) => setParsingRules(prev => ({
                        ...prev,
                        enableSubjectTypeDetection: e.target.checked
                      }))}
                    />
                    啟用科目類型檢測
                  </label>
                </div>
                
                <div className="option-item">
                  <label>
                    <input
                      type="checkbox"
                      checked={parsingRules.enableElectiveGroupExtraction}
                      onChange={(e) => setParsingRules(prev => ({
                        ...prev,
                        enableElectiveGroupExtraction: e.target.checked
                      }))}
                    />
                    啟用選修組別提取
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'testing' && (
          <div className="testing-section">
            <h3>🧪 解析測試工具</h3>
            
            <div className="test-controls">
              <div className="sample-files">
                <h4>📄 載入範例文件</h4>
                <div className="sample-buttons">
                  <button
                    onClick={() => loadSampleFile('teacher')}
                    className="sample-btn"
                  >
                    👨‍🏫 教師時間表範例
                  </button>
                  <button
                    onClick={() => loadSampleFile('class')}
                    className="sample-btn"
                  >
                    🏛️ 班級時間表範例
                  </button>
                </div>
              </div>

              <div className="test-input">
                <h4>📝 測試內容</h4>
                <textarea
                  value={testContent}
                  onChange={(e) => setTestContent(e.target.value)}
                  placeholder="在此貼上時間表腳本內容進行測試..."
                  className="test-textarea"
                  rows={15}
                />
              </div>

              <div className="test-actions">
                <button
                  onClick={testParsing}
                  disabled={isTesting || !testContent.trim()}
                  className="test-btn"
                >
                  {isTesting ? '🔄 解析中...' : '🧪 開始測試'}
                </button>
                <button
                  onClick={() => {
                    setTestContent('');
                    setTestResult(null);
                    setTestError('');
                  }}
                  className="clear-btn"
                >
                  🗑️ 清除
                </button>
              </div>
            </div>

            {testError && (
              <div className="test-error">
                <h4>❌ 解析錯誤</h4>
                <pre className="error-content">{testError}</pre>
              </div>
            )}

            {testResult && (
              <div className="test-result">
                <h4>✅ 解析結果</h4>
                <div className="result-summary">
                  <p><strong>教師代碼:</strong> {testResult.teacherCode}</p>
                  <p><strong>解析天數:</strong> {Object.keys(testResult.schedule).length}</p>
                  <p><strong>班級角色:</strong> {testResult.classRoles ? 
                    (testResult.classRoles.isClassMaster ? '班主任' : 
                     testResult.classRoles.isViceClassMaster ? '副班主任' : '無') : '無'}</p>
                </div>
                
                <div className="result-details">
                  <h5>📊 詳細結果</h5>
                  {Object.entries(testResult.schedule).map(([day, periods]) => (
                    <div key={day} className="day-result">
                      <h6>{day}</h6>
                      <div className="periods-list">
                        {periods.map((period, index) => (
                          <div key={index} className="period-item">
                            <span className="period-info">
                              期段 {period.period} (序列: {period.period_sequence})
                            </span>
                            <span className="period-subject">
                              {period.classes?.[0]} {period.subject}
                            </span>
                            {period.rooms?.[0] && (
                              <span className="period-room">課室: {period.rooms[0]}</span>
                            )}
                            {period.teachers?.[0] && (
                              <span className="period-teacher">教師: {period.teachers[0]}</span>
                            )}
                            {period.subject_type && (
                              <span className={`period-type ${period.subject_type}`}>
                                {period.subject_type}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'examples' && (
          <div className="examples-section">
            <h3>📚 腳本格式範例</h3>
            
            <div className="format-examples">
              <div className="example-card">
                <h4>👨‍🏫 教師個人時間表格式</h4>
                <div className="format-description">
                  <p>用於解析教師個人時間表腳本文件，支援多種格式和協作教學安排。</p>
                </div>
                <div className="format-patterns">
                  <h5>基本格式:</h5>
                  <code>class_id*subject-teacher_code-classroom</code>
                  
                  <h5>協作教學格式:</h5>
                  <code>class_id*subject-teacher_code/co_teacher1/co_teacher2-classroom</code>
                  
                  <h5>多課室格式:</h5>
                  <code>class_id*subject-teacher_code-classroom1/classroom2</code>
                </div>
                
                <div className="format-example">
                  <h5>範例內容:</h5>
                  <pre className="example-content">{sampleFiles.teacher}</pre>
                </div>
              </div>

              <div className="example-card">
                <h4>🏛️ 班級時間表格式</h4>
                <div className="format-description">
                  <p>用於解析班級時間表腳本文件，支援標準課程安排和特殊期段。</p>
                </div>
                <div className="format-patterns">
                  <h5>基本格式:</h5>
                  <code>Period X (time): subject - teacher - room</code>
                  
                  <h5>特殊期段格式:</h5>
                  <code>Period X (time): subject - teacher - room - notes</code>
                </div>
                
                <div className="format-example">
                  <h5>範例內容:</h5>
                  <pre className="example-content">{sampleFiles.class}</pre>
                </div>
              </div>

              <div className="example-card">
                <h4>🔧 解析規則說明</h4>
                <div className="rules-explanation">
                  <h5>分隔符說明:</h5>
                  <ul>
                    <li><code>*</code> - 分隔班級ID和科目</li>
                    <li><code>-</code> - 分隔不同實體</li>
                    <li><code>/</code> - 分隔多個選項</li>
                    <li><code>&lt;&gt;</code> - 包圍教師代碼</li>
                  </ul>
                  
                  <h5>特殊標記:</h5>
                  <ul>
                    <li><code>""</code> - 空期段，不創建UI條目</li>
                    <li><code>TBC</code> - 待確認，用於管理類型科目</li>
                    <li><code>X1/X2</code> - 選修組別標識</li>
                    <li><code>會議/同儕</code> - 管理類型科目</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'advanced' && (
          <div className="advanced-section">
            <h3>🔬 進階設定</h3>
            
            <div className="advanced-options">
              <div className="option-group">
                <h4>📤 配置管理</h4>
                <div className="config-actions">
                  <button
                    onClick={exportConfiguration}
                    className="action-btn export"
                  >
                    📤 導出配置
                  </button>
                  <label className="action-btn import">
                    📥 導入配置
                    <input
                      type="file"
                      accept=".json"
                      onChange={importConfiguration}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>

              <div className="option-group">
                <h4>🔧 高級選項</h4>
                <div className="advanced-checkboxes">
                  <label>
                    <input
                      type="checkbox"
                      checked={showAdvanced}
                      onChange={(e) => setShowAdvanced(e.target.checked)}
                    />
                    顯示進階設定選項
                  </label>
                </div>
              </div>

              {showAdvanced && (
                <div className="advanced-settings">
                  <div className="setting-group">
                    <h5>正則表達式調試</h5>
                    <label>
                      <input type="checkbox" />
                      啟用正則表達式調試模式
                    </label>
                  </div>
                  
                  <div className="setting-group">
                    <h5>性能優化</h5>
                    <label>
                      <input type="checkbox" />
                      啟用解析緩存
                    </label>
                    <label>
                      <input type="checkbox" />
                      並行解析模式
                    </label>
                  </div>
                  
                  <div className="setting-group">
                    <h5>錯誤處理</h5>
                    <label>
                      <input type="checkbox" />
                      嚴格模式 (遇到錯誤時停止解析)
                    </label>
                    <label>
                      <input type="checkbox" />
                      寬鬆模式 (跳過錯誤繼續解析)
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParsingAlgorithmSettings;
